import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, Renderer2, NgZone, ViewChild, Input } from '@angular/core';
import * as _ from 'lodash';
import { ScheduleTreeEditModel } from 'src/app/pages/main/_models/Monitoring/template';

@Component({
    selector: 'app-schedule-tree',
    template: `
      <div class="schedule-tree-wrapper">
        <div class="schedule-tree"></div>
        <div #customTooltip class="custom-tree-tooltip">
          <div class="tooltip-content"></div>
        </div>
      </div>
    `,
    styleUrl: './schedule-tree.component.scss',
    standalone: false
})
export class ScheduleTreeComponent implements AfterViewInit, OnDestroy {
  @Input() isGuardTourTree = false;
  @Output() hasChangedEvent: EventEmitter<boolean> = new EventEmitter();
  @ViewChild('customTooltip', { static: true }) customTooltip: ElementRef;

  set editData(val: any) {
    this._editData = val;
    this.refreshTree();
  }

  private _$tree: JQuery;
  private _editData: ScheduleTreeEditModel;
  private _createdTreeBefore: any;
  private _withCheckbox: boolean = true;
  private tooltipTimeout: any;
  private currentMouseX: number = 0;
  private currentMouseY: number = 0;

  constructor(
    private _element: ElementRef,
    private renderer: Renderer2,
  ) {
    this._editData = {entities: [], selectedEntityNames: []};
  }

  ngAfterViewInit(): void {
    this._$tree = $(this._element.nativeElement).find('.schedule-tree');
    this.refreshTree();
  }

  ngOnDestroy(): void {
    if (this._$tree) {
      this._$tree.jstree('destroy');
      this._$tree.off('.jstree');
    }
    
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }
  }

  setDisplayCheckbox(isDisplayed: boolean): void {
    this._withCheckbox = isDisplayed;
  }

  getSelectedEntityNames(): string[] {
    if (!this._$tree || !this._createdTreeBefore) {
        return [];
    }

    let entityNames = [];

    let selectedEntities = this._$tree.jstree('get_selected', true);
    selectedEntities = selectedEntities.filter((node: any) => !node.state.disabled);
    for (let i = 0; i < selectedEntities.length; i++) {
        entityNames.push(selectedEntities[i].original.id);
    }

    return entityNames;
  }

  refreshTree(): void {
    let self = this;

    if (this._createdTreeBefore) {
        this._$tree.jstree('destroy');
    }

    this._createdTreeBefore = false;

    if (!this._editData || !this._$tree) {
        return;
    }

    const isNvrEnabledMap: Record<string, boolean> = {};
    this._editData.entities.forEach((entity: any) => {
        isNvrEnabledMap[entity.name] = entity.isGuardTour && entity.isMonitored;
    });

    let treeData = _.map(this._editData.entities, (item) => {
        let tooltipText = '';
        let cssClass = '';
        let isDisabled = true;
        let hasWarning = false;

        let baseText = item.templateName ? item.displayName + ' (' + item.templateName + ')' : item.displayName;

        if (this.isGuardTourTree) {
            const isSite = item.name.startsWith('Site');
            const isCamera = item.name.startsWith('Camera');

            if (!isSite) {
                const { isGuardTour, isMonitored } = item;

                if (!isGuardTour && !isMonitored) {
                    tooltipText = 'Non-Guard Tour & Non-Monitored';
                    cssClass = 'non-guardtour-non-monitored';
                } else if (!isGuardTour) {
                    tooltipText = 'Non-Guard Tour';
                    cssClass = 'non-guardtour';
                } else if (!isMonitored) {
                    tooltipText = 'Non-Monitored';
                    cssClass = 'non-monitored';
                }

                if (tooltipText) {
                    isDisabled = false;
                }
            }

            if (isCamera) {
                const parentIsGuardTour = isNvrEnabledMap[item.parentName];
                if (!parentIsGuardTour && item.isGuardTour) {
                    cssClass += ' text-yellow';
                    baseText += ' <span class="material-icons" style="font-size:15px !important; position:relative; top:2px;">warning</span>';
                    tooltipText = 'Camera is Guard Tour Enabled';
                    hasWarning = true;
                    isDisabled = false;
                }
            }

            if (!isDisabled && !hasWarning) {
                baseText += ' <span class="material-icons" style="font-size:15px !important; position:relative; top:3px;">info</span>';
            }
        }

        let fullText = baseText;

        return {
            id: item.name,
            parent: item.parentName ? item.parentName : '#',
            text: fullText,
            state: {
                disabled: !isDisabled,
                opened: item.parentName ? false : true,
                selected: _.includes(self._editData.selectedEntityNames, item.name)
            },
            original: {
                id: item.name,
                displayName: item.displayName,
                templateName: item.templateName,
                parentName: item.parentName,
                tooltipText
            },
            li_attr: {                
                class: cssClass
            },
            a_attr: {
                class: cssClass,
            },
        };
    });

    if (this._withCheckbox) {
        this._$tree.jstree({
            'core': {
                data: treeData,
                themes: {
                  responsive: false,
                  icons: false
                }
            },
            'checkbox': {
                keep_selected_style: false,
                three_state: false,
                cascade: ''
            },
            plugins: ['checkbox', 'search']
        });
    }
    else {
        this._$tree.jstree({
            'core': {
                data: treeData,
                themes: {
                  responsive: false,
                  icons: false
                }
            }
        });
    }

    this._createdTreeBefore = true;

    // Attach tooltip events after tree is ready
    this._$tree.on('ready.jstree', () => {
      this.attachTooltipEvents();
    });

    // Re-attach tooltips after search
    this._$tree.on('search.jstree', () => {
      setTimeout(() => this.attachTooltipEvents(), 100);
    });

    // Re-attach tooltips after open/close nodes
    this._$tree.on('after_open.jstree after_close.jstree', () => {
      setTimeout(() => this.attachTooltipEvents(), 50);
    });

    let inTreeChangeEvent = false;

    this._$tree.on('changed.jstree', (e, data) => {
        if (!data.node) {
            return;
        }

        let wasInTreeChangeEvent = inTreeChangeEvent;
        if (!wasInTreeChangeEvent) {
            inTreeChangeEvent = true;
            this.hasChangedEvent.emit(true);
        }

        let childrenNodes;

        if (data.node.state.selected) {
            childrenNodes = $.makeArray(self._$tree.jstree('get_node', data.node).children);
            self._$tree.jstree('select_node', childrenNodes);

        } else {
            childrenNodes = $.makeArray(self._$tree.jstree('get_node', data.node).children);
            self._$tree.jstree('deselect_node', childrenNodes);
        }

        if (!wasInTreeChangeEvent) {
            inTreeChangeEvent = false;
            this.hasChangedEvent.emit(true);
        }
    });

    this._$tree.on('beforecollapse', function(node, event) {
        if (event.type == 'dblclick') { return false; }
    });
  }

  /**
   * Attach tooltip events to tree nodes
   */
  private attachTooltipEvents(): void {
    // Remove existing tooltip events to prevent duplicates
    this._$tree.off('mouseenter.customTooltip mouseleave.customTooltip mousemove.customTooltip');
    
    const self = this;
    
    // Mouse enter event
    this._$tree.on('mouseenter.customTooltip', '.jstree-anchor', function(e) {
      const nodeElement = $(this).closest('.jstree-node');
      const nodeId = nodeElement.attr('id');
      
      if (nodeId) {
        const nodeData = self._$tree.jstree('get_node', nodeId);

        if (nodeData && nodeData.original && nodeData.original?.original?.tooltipText) {
          self.showTooltip(e, nodeData.original);
        }
      }
    });

    // Mouse move event (to update position)
    this._$tree.on('mousemove.customTooltip', '.jstree-anchor', function(e) {
      self.currentMouseX = e.clientX || e.originalEvent?.clientX || 0;
      self.currentMouseY = e.clientY || e.originalEvent?.clientY || 0;
      
      // Update position if tooltip is visible
      if (self.customTooltip?.nativeElement.classList.contains('show')) {
        self.updateTooltipPosition();
      }
    });

    // Mouse leave event
    this._$tree.on('mouseleave.customTooltip', '.jstree-anchor', function() {
      self.hideTooltip();
    });
  }

  /**
   * Show tooltip with content
   */
  private showTooltip(event: any, nodeData: any): void {
    if (!this.customTooltip || !this.customTooltip.nativeElement) {
      console.error('Tooltip element not found!');
      return;
    }
    
    // Clear existing timeout
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }

    // Store initial mouse position
    this.currentMouseX = event.clientX || event.originalEvent?.clientX || 0;
    this.currentMouseY = event.clientY || event.originalEvent?.clientY || 0;

    // Build tooltip content
    const tooltipContent = nodeData.original.tooltipText;
    
    const tooltipElement = this.customTooltip.nativeElement;
    const contentElement = tooltipElement.querySelector('.tooltip-content');
    
    if (contentElement) {
      contentElement.textContent = tooltipContent;
    }

    // Show tooltip with delay
    this.tooltipTimeout = setTimeout(() => {
      
      // Position tooltip first
      this.updateTooltipPosition();
      
      // Show it using direct style manipulation
      this.renderer.setStyle(tooltipElement, 'display', 'block');
      this.renderer.addClass(tooltipElement, 'show');
    }, 300);
  }

  /**
   * Update tooltip position
   */
  private updateTooltipPosition(): void {
    if (!this.customTooltip || !this.customTooltip.nativeElement) {
      return;
    }
    
    const tooltipElement = this.customTooltip.nativeElement;
    
    // Temporarily make visible to get dimensions
    const wasHidden = tooltipElement.style.display === 'none';
    if (wasHidden) {
      this.renderer.setStyle(tooltipElement, 'visibility', 'hidden');
      this.renderer.setStyle(tooltipElement, 'display', 'block');
    }
    
    // Get tooltip dimensions
    const tooltipRect = tooltipElement.getBoundingClientRect();
    
    // Reset if was hidden
    if (wasHidden) {
      this.renderer.setStyle(tooltipElement, 'visibility', 'visible');
    }
    
    // Calculate position (15px offset from cursor)
    let left = this.currentMouseX + 15;
    let top = this.currentMouseY - (tooltipRect.height / 2);
    
    // Adjust if tooltip goes off screen (right edge)
    if (left + tooltipRect.width > window.innerWidth) {
      left = this.currentMouseX - tooltipRect.width - 15;
    }
    
    // Adjust if tooltip goes off screen (top)
    if (top < 0) {
      top = 10;
    }
    
    // Adjust if tooltip goes off screen (bottom)
    if (top + tooltipRect.height > window.innerHeight) {
      top = window.innerHeight - tooltipRect.height - 10;
    }
    
    // Apply position
    this.renderer.setStyle(tooltipElement, 'left', `${left}px`);
    this.renderer.setStyle(tooltipElement, 'top', `${top}px`);
  }

  /**
   * Hide tooltip
   */
  private hideTooltip(): void {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }
    
    if (!this.customTooltip || !this.customTooltip.nativeElement) {
      return;
    }
    
    const tooltipElement = this.customTooltip.nativeElement;
    this.renderer.removeClass(tooltipElement, 'show');
    this.renderer.setStyle(tooltipElement, 'display', 'none');
  }

  /**
   * Build tooltip text from node data
   */
  private buildTooltipText(nodeData: any): string {
    let tooltipParts: string[] = [];
    
    // Display Name
    if (nodeData.original.displayName) {
      tooltipParts.push(`Name: ${nodeData.original.displayName}`);
    }
    
    // Template Name
    if (nodeData.original.templateName) {
      tooltipParts.push(`Template: ${nodeData.original.templateName}`);
    }
    
    // Parent Name
    if (nodeData.original.parentName) {
      tooltipParts.push(`Parent: ${nodeData.original.parentName}`);
    }
    
    // Return formatted tooltip
    return tooltipParts.length > 0 
      ? tooltipParts.join('\n') 
      : nodeData.original.displayName || 'No information available';
  }

  search(value: string) {
    let self = this;
    if(self._$tree) {
      self._$tree.jstree(true).search(value);
    }
  }
}